export type UserListAction = 'suspend' | 'activate' | 'restore' | 'delete';

type ToastCopy = {
  title: string;
  description?: string;
  variant: 'success' | 'danger';
};

type ConfirmCopy = {
  title: string;
  description: string;
  confirmLabel: string;
  variant: 'danger' | 'primary';
};

const DUPLICATE_EMAIL_MESSAGE = 'Ya existe un usuario con ese correo electrónico';

export function userFormErrorToast(error: unknown): ToastCopy {
  const raw = error instanceof Error ? error.message.trim() : '';
  const message =
    raw.toLowerCase() === 'failed to fetch' || raw.toLowerCase().includes('networkerror')
      ? 'No se pudo conectar con el servidor'
      : raw;
  if (message.includes(DUPLICATE_EMAIL_MESSAGE)) {
    return { title: DUPLICATE_EMAIL_MESSAGE, variant: 'danger' };
  }
  return {
    title: message || 'No se pudo guardar el usuario',
    variant: 'danger',
  };
}

export type UserImportOutcome = {
  imported: number;
  skipped: Array<{ reason: string }>;
  errors: string[];
};

export function userImportRequestErrorToast(error: unknown): ToastCopy {
  const copy = userFormErrorToast(error);
  if (copy.title === 'No se pudo guardar el usuario') {
    return { title: 'No se pudo importar el usuario', variant: 'danger' };
  }
  return copy;
}

export function userImportToasts(result: UserImportOutcome): ToastCopy[] {
  const problems = [
    ...result.skipped.map((row) => row.reason.trim()).filter(Boolean),
    ...result.errors.map((line) => line.trim()).filter(Boolean),
  ];
  const toasts: ToastCopy[] = [];

  if (result.imported === 1) {
    toasts.push({ title: 'Usuario creado exitosamente', variant: 'success' });
  } else if (result.imported > 1) {
    toasts.push({ title: `${result.imported} usuarios creados exitosamente`, variant: 'success' });
  }

  if (problems.length === 0) {
    if (toasts.length === 0) {
      toasts.push({ title: 'No se pudo importar el usuario', variant: 'danger' });
    }
    return toasts;
  }

  if (problems.length === 1 && result.imported === 0) {
    toasts.push({ title: problems[0], variant: 'danger' });
    return toasts;
  }

  toasts.push({
    title: result.imported === 0 ? 'No se pudieron importar los usuarios' : 'No se importaron todos los usuarios',
    description: problems.length === 1 ? problems[0] : problems.slice(0, 3).join(' '),
    variant: 'danger',
  });
  return toasts;
}

export function roleFormSuccessToast(isEdit: boolean): ToastCopy {
  return {
    title: isEdit ? 'Rol editado exitosamente' : 'Rol creado exitosamente',
    variant: 'success',
  };
}

export function permissionMatrixEditSuccessToast(): ToastCopy {
  return { title: 'Permisos editados exitosamente', variant: 'success' };
}

export function roleDeleteSuccessToast(): ToastCopy {
  return { title: 'Rol eliminado exitosamente', variant: 'success' };
}

export function groupFormSuccessToast(isEdit: boolean): ToastCopy {
  return {
    title: isEdit ? 'Grupo editado exitosamente' : 'Grupo creado exitosamente',
    variant: 'success',
  };
}

export function groupDeleteSuccessToast(): ToastCopy {
  return { title: 'Grupo eliminado exitosamente', variant: 'success' };
}

export type CourseWriteAction = 'create' | 'edit' | 'delete' | 'publish' | 'archive';

export type BuilderSuccessAction =
  | 'save'
  | 'section-create'
  | 'section-duplicate'
  | 'section-delete'
  | 'activity-add'
  | 'label-add'
  | 'activity-delete'
  | 'settings';

export function builderSuccessToast(action: BuilderSuccessAction): ToastCopy {
  const title: Record<BuilderSuccessAction, string> = {
    save: 'Cambios guardados',
    'section-create': 'Sección creada',
    'section-duplicate': 'Sección duplicada',
    'section-delete': 'Sección eliminada',
    'activity-add': 'Actividad añadida',
    'label-add': 'Etiqueta añadida',
    'activity-delete': 'Actividad eliminada',
    settings: 'Configuración guardada',
  };
  return { title: title[action], variant: 'success' };
}

export function mediaUploadSuccessToast(count = 1): ToastCopy {
  if (count > 1) {
    return { title: `${count} archivos subidos correctamente`, variant: 'success' };
  }
  return { title: 'El archivo se subió correctamente', variant: 'success' };
}

export function mediaEditSuccessToast(): ToastCopy {
  return { title: 'El archivo se editó correctamente', variant: 'success' };
}

export function mediaDeleteSuccessToast(): ToastCopy {
  return { title: 'El archivo se eliminó correctamente', variant: 'success' };
}

export function contractTemplateSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Plantilla de contrato creada exitosamente',
    edit: 'Plantilla de contrato editada exitosamente',
    delete: 'Plantilla de contrato eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function contractGeneratedDeleteSuccessToast(): ToastCopy {
  return { title: 'Contrato eliminado exitosamente', variant: 'success' };
}

export function contractAcceptSuccessToast(name?: string): ToastCopy {
  const trimmed = name?.trim();
  return {
    title: 'Contrato confirmado correctamente',
    description: trimmed ? `¡Bienvenido, ${trimmed}!` : '¡Bienvenido!',
    variant: 'success',
  };
}

export function credentialTemplateSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Plantilla de credencial creada exitosamente',
    edit: 'Plantilla de credencial editada exitosamente',
    delete: 'Plantilla de credencial eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function credentialSuccessToast(action: 'issue' | 'edit' | 'delete'): ToastCopy {
  const title = {
    issue: 'Credencial emitida exitosamente',
    edit: 'Credencial editada exitosamente',
    delete: 'Credencial eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function legalPolicySuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Política legal creada exitosamente',
    edit: 'Política legal editada exitosamente',
    delete: 'Política legal eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function librarySuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Objeto creado exitosamente',
    edit: 'Objeto editado exitosamente',
    delete: 'Objeto eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function h5pSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'H5P creado exitosamente',
    edit: 'H5P editado exitosamente',
    delete: 'H5P eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function assessmentAttemptSuccessToast(): ToastCopy {
  return { title: 'Intento enviado correctamente', variant: 'success' };
}

export function assessmentSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Evaluación creada exitosamente',
    edit: 'Evaluación editada exitosamente',
    delete: 'Evaluación eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function questionBankSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Banco de preguntas creado exitosamente',
    edit: 'Banco de preguntas editado exitosamente',
    delete: 'Banco de preguntas eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function questionSuccessToast(action: 'create' | 'edit' | 'delete', count = 1): ToastCopy {
  if (action === 'delete' && count > 1) {
    return { title: `${count} preguntas eliminadas exitosamente`, variant: 'success' };
  }
  const title = {
    create: 'Pregunta creada exitosamente',
    edit: 'Pregunta editada exitosamente',
    delete: 'Pregunta eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function customFieldGroupSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Grupo de campos creado exitosamente',
    edit: 'Grupo de campos editado exitosamente',
    delete: 'Grupo de campos eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function customFieldOrderSuccessToast(): ToastCopy {
  return { title: 'Orden del formulario guardado', variant: 'success' };
}

export function customFieldSuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Campo personalizado creado exitosamente',
    edit: 'Campo personalizado editado exitosamente',
    delete: 'Campo personalizado eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function categorySuccessToast(action: 'create' | 'edit' | 'delete'): ToastCopy {
  const title = {
    create: 'Categoría creada exitosamente',
    edit: 'Categoría editada exitosamente',
    delete: 'Categoría eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function forumThreadCreateSuccessToast(): ToastCopy {
  return { title: 'Hilo creado exitosamente', variant: 'success' };
}

export function forumThreadDeleteSuccessToast(): ToastCopy {
  return { title: 'Hilo eliminado exitosamente', variant: 'success' };
}

export function forumThreadDeleteConfirm(title: string): ConfirmCopy {
  const quoted = title.trim();
  return {
    title: 'Eliminar hilo',
    description: quoted
      ? `¿Eliminar el hilo «${quoted}»? Esta acción no se puede deshacer.`
      : '¿Eliminar este hilo? Esta acción no se puede deshacer.',
    confirmLabel: 'Eliminar',
    variant: 'danger',
  };
}

export function supportTicketSuccessToast(): ToastCopy {
  return { title: 'Ticket creado exitosamente', variant: 'success' };
}

export function supportTicketCloseSuccessToast(): ToastCopy {
  return { title: 'Ticket cerrado exitosamente', variant: 'success' };
}

export function enrollmentSuccessToast(action: 'create' | 'edit' | 'delete', count = 1): ToastCopy {
  if (action === 'create' && count > 1) {
    return { title: `${count} matrículas creadas exitosamente`, variant: 'success' };
  }
  const title = {
    create: 'Matrícula creada exitosamente',
    edit: 'Matrícula editada exitosamente',
    delete: 'Matrícula eliminada exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export type SiteAssetAction = 'add' | 'edit' | 'delete';

export function siteAssetSuccessToast(action: SiteAssetAction): ToastCopy {
  const title = {
    add: 'Asset agregado exitosamente',
    edit: 'Asset editado exitosamente',
    delete: 'Asset eliminado exitosamente',
  } as const;
  return { title: title[action], variant: 'success' };
}

export function courseMissingCategoryToast(): ToastCopy {
  return { title: 'Cree una categoría primero', variant: 'danger' };
}

export function courseSuccessToast(action: CourseWriteAction): ToastCopy {
  const title: Record<CourseWriteAction, string> = {
    create: 'Curso creado exitosamente',
    edit: 'Curso editado exitosamente',
    delete: 'Curso eliminado exitosamente',
    publish: 'Curso publicado exitosamente',
    archive: 'Curso archivado exitosamente',
  };
  return { title: title[action], variant: 'success' };
}

export function roleActionErrorToast(error: unknown, fallback: string): ToastCopy {
  const copy = userFormErrorToast(error);
  if (copy.title === 'No se pudo guardar el usuario') {
    return { title: fallback, variant: 'danger' };
  }
  return copy;
}

export function userFormSuccessToast(isEdit: boolean): ToastCopy {
  return {
    title: isEdit ? 'Usuario editado exitosamente' : 'Usuario creado exitosamente',
    variant: 'success',
  };
}

export function userListSuccessToast(action: UserListAction): ToastCopy {
  const title: Record<UserListAction, string> = {
    suspend: 'Usuario suspendido exitosamente',
    activate: 'Usuario activado exitosamente',
    restore: 'Usuario restaurado exitosamente',
    delete: 'Usuario eliminado exitosamente',
  };
  return { title: title[action], variant: 'success' };
}

export function userListErrorToast(action: UserListAction, error: unknown): ToastCopy {
  const title: Record<UserListAction, string> = {
    suspend: 'No se pudo suspender el usuario',
    activate: 'No se pudo activar el usuario',
    restore: 'No se pudo restaurar el usuario',
    delete: 'No se pudo eliminar el usuario',
  };
  const message = error instanceof Error ? error.message.trim() : '';
  return {
    title: title[action],
    description: message && message !== title[action] ? message : undefined,
    variant: 'danger',
  };
}

export function userListConfirm(action: UserListAction, name: string): ConfirmCopy {
  const quoted = `«${name}»`;
  if (action === 'suspend') {
    return {
      title: 'Suspender usuario',
      description: `¿Suspender a ${quoted}? No podrá iniciar sesión hasta que lo actives.`,
      confirmLabel: 'Suspender',
      variant: 'danger',
    };
  }
  if (action === 'activate') {
    return {
      title: 'Activar usuario',
      description: `¿Activar a ${quoted}?`,
      confirmLabel: 'Activar',
      variant: 'primary',
    };
  }
  if (action === 'restore') {
    return {
      title: 'Restaurar usuario',
      description: `¿Restaurar a ${quoted}? Volverá a estar disponible en el listado.`,
      confirmLabel: 'Restaurar',
      variant: 'primary',
    };
  }
  return {
    title: 'Eliminar usuario',
    description: `¿Está seguro de eliminar a ${quoted}?`,
    confirmLabel: 'Eliminar',
    variant: 'danger',
  };
}
